import React, { useState } from 'react';
import { Mail, Check, MapPin, Clock } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import Footer from './Footer';
import { useTheme } from '@/context/ThemeContext';

const serviceOptions = ['AI Agents', 'Process Automation', 'Enterprise Consulting', 'Other'];
const budgetOptions = ['< $50K', '$50K – $100K', '$100K – $200K', '> $200K'];

const ContactUsPage = () => {
    const [formState, setFormState] = useState({
        name: '',
        email: '',
        company: '',
        service: '',
        budget: '',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const { resolved } = useTheme();
    const isLight = resolved === 'light';

    const header = useScrollReveal();
    const form = useScrollReveal();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            const response = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    access_key: 'bf6ec408-ca4d-48f3-98df-1effbc73a151',
                    name: formState.name,
                    email: formState.email,
                    company: formState.company,
                    service: formState.service,
                    budget: formState.budget,
                    message: formState.message,
                    subject: `New inquiry from ${formState.name} — ${formState.company}`,
                }),
            });

            const data = await response.json();
            if (data.success) {
                setSubmitted(true);
            } else {
                setError('Something went wrong. Please try again or email us directly.');
            }
        } catch {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
            <section className="min-h-[calc(100vh-2.5rem)] flex items-start pt-20 pb-16 md:pt-24 md:pb-20">
                <div className="max-w-6xl w-full mx-auto px-4 md:px-6 relative">

                    {/* DECORATIVE FLOW — sleek accent line guiding to form */}
                    <div
                        className="absolute top-8 -right-[40px] w-[120px] h-[500px] pointer-events-none hidden xl:block z-20"
                        style={{ opacity: isLight ? 0.15 : 0.18 }}
                    >

                    </div>

                    {/* Grid Card — same aesthetic as landing page hero */}
                    <div className="relative rounded-none border border-overlay/[0.08] bg-overlay/[0.01]">

                        {/* Grid lines overlay */}
                        <div
                            className="absolute inset-0 pointer-events-none hidden md:block"
                            style={{
                                backgroundImage: isLight
                                    ? 'repeating-linear-gradient(90deg, rgba(0,0,0,0.04) 0px, rgba(0,0,0,0.04) 1px, transparent 1px, transparent)'
                                    : 'repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent)',
                                backgroundSize: '12.5% 100%',
                            }}
                        />
                        {/* Top horizontal line */}
                        <div className="absolute top-12 left-0 w-full h-px bg-overlay/[0.04] pointer-events-none" />

                        {/* Corner markers */}
                        <span className="absolute top-3 left-4 text-overlay/[0.15] text-sm font-mono select-none z-20">+</span>
                        <span className="absolute bottom-3 right-4 text-overlay/[0.15] text-sm font-mono select-none z-20">+</span>

                        <div className="relative z-10 px-6 md:px-14 lg:px-20 pt-16 md:pt-20 pb-10 md:pb-16">

                            {/* Two-column layout */}
                            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-20">

                                {/* Left: Header + info */}
                                <div
                                    ref={header.ref}
                                    className={`transition-all duration-600 ${header.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                                >
                                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-overlay/[0.08] bg-overlay/[0.03] backdrop-blur-sm mb-7">
                                        <span className="text-[13px] font-medium text-muted-foreground">Get in Touch</span>
                                    </div>

                                    <h1 className="text-[2.3rem] sm:text-[2.8rem] lg:text-[3.2rem] font-semibold tracking-[-0.04em] leading-[0.96] mb-6 whitespace-normal">
                                        Let's build something
                                        <br />
                                        <span className="text-gradient-primary">great together</span>
                                    </h1>

                                    <p className="text-[15px] md:text-base text-muted-foreground font-light leading-relaxed">
                                        Tell us about your project and we'll get back to you within 24 hours.
                                    </p>

                                    {/* Contact details */}
                                    <div className="mt-10 pt-8 border-t border-overlay/[0.06] space-y-6">
                                        {/* Email */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full border border-overlay/[0.08] bg-overlay/[0.03] flex items-center justify-center flex-shrink-0">
                                                <Mail size={14} className="text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-[11px] text-muted-foreground mb-0.5 uppercase tracking-widest">Prefer email?</p>
                                                <a
                                                    href="mailto:info@antz.ai"
                                                    className="text-[14px] font-medium text-foreground hover:text-primary transition-colors"
                                                >
                                                    info@antz.ai
                                                </a>
                                            </div>
                                        </div>

                                        {/* Address */}
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-full border border-overlay/[0.08] bg-overlay/[0.03] flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <MapPin size={14} className="text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-[11px] text-muted-foreground mb-0.5 uppercase tracking-widest">Office</p>
                                                <p className="text-[14px] font-medium text-foreground leading-snug">
                                                    S2 Techno Park, Capital Pk Rd,<br />
                                                    VIP Hills, Silicon Valley, Madhapur,<br />
                                                    Hyderabad, Telangana 500081
                                                </p>
                                            </div>
                                        </div>

                                        {/* Business Hours */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full border border-overlay/[0.08] bg-overlay/[0.03] flex items-center justify-center flex-shrink-0">
                                                <Clock size={14} className="text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-[11px] text-muted-foreground mb-0.5 uppercase tracking-widest">Business hours</p>
                                                <p className="text-[14px] font-medium text-foreground">
                                                    Open &middot; Closes at 5 PM
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right: Form */}
                                <div
                                    ref={form.ref}
                                    className={`transition-all duration-600 ${form.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                                    style={{ animationDelay: '150ms' }}
                                >
                                    {!submitted ? (
                                        <form onSubmit={handleSubmit} className="space-y-5">

                                            {/* Name + Email */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <input
                                                    type="text"
                                                    required
                                                    value={formState.name}
                                                    onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                                                    placeholder="Your name *"
                                                    className="w-full px-4 py-3.5 rounded-lg border border-overlay/[0.08] bg-overlay/[0.03] text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 transition-colors placeholder:text-muted-foreground/50"
                                                />
                                                <div className="relative">
                                                    <input
                                                        type="email"
                                                        required
                                                        value={formState.email}
                                                        onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                                                        placeholder="Work email *"
                                                        className="w-full pl-4 pr-10 py-3.5 rounded-lg border border-overlay/[0.08] bg-overlay/[0.03] text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 transition-colors placeholder:text-muted-foreground/50"
                                                    />
                                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/50">
                                                        <Mail size={14} strokeWidth={2} />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Company */}
                                            <input
                                                type="text"
                                                required
                                                value={formState.company}
                                                onChange={(e) => setFormState({ ...formState, company: e.target.value })}
                                                placeholder="Company name *"
                                                className="w-full px-4 py-3.5 rounded-lg border border-overlay/[0.08] bg-overlay/[0.03] text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 transition-colors placeholder:text-muted-foreground/50"
                                            />

                                            {/* Service Interest — pill cards */}
                                            <div>
                                                <h3 className="text-[11px] font-medium text-muted-foreground mb-3 uppercase tracking-widest">
                                                    What are you interested in? *
                                                </h3>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {serviceOptions.map((option) => (
                                                        <label
                                                            key={option}
                                                            className={`flex items-center gap-2.5 cursor-pointer px-4 py-3 rounded-lg border transition-all duration-200 ${formState.service === option
                                                                    ? 'border-primary/40 bg-primary/[0.06] text-foreground'
                                                                    : 'border-overlay/[0.08] bg-overlay/[0.02] text-muted-foreground hover:border-overlay/[0.15] hover:text-foreground'
                                                                }`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="service"
                                                                required
                                                                value={option}
                                                                checked={formState.service === option}
                                                                onChange={(e) => setFormState({ ...formState, service: e.target.value })}
                                                                className="sr-only"
                                                            />
                                                            <div className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 transition-all ${formState.service === option
                                                                    ? 'border-primary bg-primary'
                                                                    : 'border-overlay/[0.2]'
                                                                }`} />
                                                            <span className="text-[13px] font-medium">{option}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Budget — pill cards */}
                                            <div>
                                                <h3 className="text-[11px] font-medium text-muted-foreground mb-3 uppercase tracking-widest">
                                                    Project budget *
                                                </h3>
                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                    {budgetOptions.map((option) => (
                                                        <label
                                                            key={option}
                                                            className={`flex items-center gap-2 cursor-pointer px-3 py-3 rounded-lg border transition-all duration-200 ${formState.budget === option
                                                                    ? 'border-primary/40 bg-primary/[0.06] text-foreground'
                                                                    : 'border-overlay/[0.08] bg-overlay/[0.02] text-muted-foreground hover:border-overlay/[0.15] hover:text-foreground'
                                                                }`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="budget"
                                                                required
                                                                value={option}
                                                                checked={formState.budget === option}
                                                                onChange={(e) => setFormState({ ...formState, budget: e.target.value })}
                                                                className="sr-only"
                                                            />
                                                            <div className={`w-3 h-3 rounded-full border-2 flex-shrink-0 transition-all ${formState.budget === option
                                                                    ? 'border-primary bg-primary'
                                                                    : 'border-overlay/[0.2]'
                                                                }`} />
                                                            <span className="text-[12px] font-medium">{option}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Message */}
                                            <div className="border-b border-overlay/[0.06] pb-6">
                                                <textarea
                                                    required
                                                    placeholder="Tell us about your project, timeline, and any other details... *"
                                                    rows={4}
                                                    value={formState.message}
                                                    onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                                                    className="w-full px-4 py-3.5 rounded-lg border border-overlay/[0.08] bg-overlay/[0.03] text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 transition-colors placeholder:text-muted-foreground/50 resize-none"
                                                />
                                            </div>

                                            {/* Error message */}
                                            {error && (
                                                <p className="text-sm text-red-500">{error}</p>
                                            )}

                                            {/* Submit row */}
                                            <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className="inline-flex items-center justify-center gap-2.5 bg-foreground text-background px-6 md:px-7 py-3 rounded-full font-medium text-[15px] hover:opacity-90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                                >
                                                    {isSubmitting ? 'Sending...' : 'Send message'}
                                                </button>
                                                <p className="text-[11px] text-muted-foreground/60">
                                                    By submitting you agree to our{' '}
                                                    <a href="/privacy-policy" className="underline hover:text-muted-foreground transition-colors">
                                                        Privacy policy
                                                    </a>
                                                </p>
                                            </div>
                                        </form>
                                    ) : (
                                        <div className="py-16 flex flex-col items-center justify-center text-center animate-reveal-up">
                                            <div className="w-14 h-14 rounded-full border border-overlay/[0.08] bg-primary/10 flex items-center justify-center mb-6">
                                                <Check className="w-6 h-6 text-primary" strokeWidth={2.5} />
                                            </div>
                                            <h2 className="text-[26px] font-semibold text-foreground tracking-[-0.03em] mb-3">
                                                We've received your request
                                            </h2>
                                            <p className="text-[15px] text-muted-foreground leading-relaxed max-w-sm">
                                                Thanks for reaching out! We'll review your project details and get back to you within{' '}
                                                <span className="font-medium text-foreground">24 hours</span>.
                                            </p>
                                            <button
                                                onClick={() => {
                                                    setSubmitted(false);
                                                    setError('');
                                                    setFormState({ name: '', email: '', company: '', service: '', budget: '', message: '' });
                                                }}
                                                className="mt-8 text-sm font-medium text-primary hover:opacity-80 transition-opacity underline underline-offset-4"
                                            >
                                                Submit another request
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ContactUsPage;
